import React, { useCallback, useEffect, useRef, useState } from 'react';
import Tooltip, { type TooltipPosition } from './Tooltip';

interface TruncateTooltipProps {
  text: string;
  className?: string;
  position?: TooltipPosition;
}

/**
 * Truncated text with tooltip only when overflow is clipped (scrollWidth > clientWidth).
 * Standard SaaS: never show a tooltip that only repeats fully visible text.
 */
const TruncateTooltip: React.FC<TruncateTooltipProps> = ({
  text,
  className = '',
  position,
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [truncated, setTruncated] = useState(false);

  const checkTruncation = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setTruncated(el.scrollWidth > el.clientWidth);
  }, []);

  useEffect(() => {
    checkTruncation();
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(checkTruncation);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, checkTruncation]);

  const element = (
    <span ref={ref} className={`block truncate min-w-0 ${className}`}>
      {text}
    </span>
  );

  if (!truncated) return element;

  return (
    <Tooltip content={text} position={position}>
      {element}
    </Tooltip>
  );
};

export default TruncateTooltip;
